import {
    PagedResult,
    ReportFilters,
    ReportStats,
    ReportType,
    getReportDataPaged,
    getReportStats,
    GROUPABLE_REPORT_TYPES,
    GroupedReportRow,
    getGroupedReport,
} from '@/lib/api/reports/reports-api'
import { DetailPopupProvider } from '@/components/detail-popup'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { MdFunctions, MdPayments, MdSchedule, MdViewList } from 'react-icons/md'
import {
    DEFAULT_DATE_BASIS,
    ReportConfig,
    saleDefaultsPatch,
    toLocalDateString,
} from '../config/report-configs'
import { defaultBillingStatusPatch } from '../config/default-billing-status'
import ColumnPicker from './column-picker'
import GroupedReportTable, { GROUPED_COLUMNS } from './grouped-report-table'
import ExportActionsComponent from './export-actions'
import PayoutBatchModal from './payout-batch-modal'
import AutomationFormModal from '../automations/components/automation-form-modal'
import ReportFiltersComponent from './report-filters'
import ReportStatsComponent from './report-stats'
import ReportTableComponent from './report-table'

interface ReportSectionProps {
    reportType: ReportType
    config: ReportConfig
    scopeFilter?: Partial<ReportFilters>
    /** Título da seção. Default: o `label` da config do tipo. */
    title?: string
    /**
     * Total de registros do filtro atual, a cada carga. A aba de produtividade usa isto
     * para calcular a comissão sem repetir a requisição que a tabela já fez.
     */
    onTotalChange?: (total: number) => void
    /**
     * Estatísticas do filtro atual, a cada carga. A aba de produtividade usa o
     * `totalRevenue` como base da comissão percentual.
     */
    onStatsChange?: (stats: ReportStats | null) => void
    /**
     * Esconde o seletor de período da seção. Quem esconde precisa injetar
     * `startDate`/`endDate` pelo `scopeFilter` — é assim que a produtividade compartilha
     * um período único entre as seções de vários tipos.
     */
    hidePeriodFilter?: boolean
    /**
     * Esconde os filtros que quem monta a seção governa de fora — é como a aba de
     * Produtividade aplica um recorte único a consultas, exames e procedimentos ao
     * mesmo tempo. Só sobram na seção os filtros exclusivos do tipo.
     */
    hideCommonFilters?: boolean
    /**
     * Status da cobrança escolhido no topo da tela como ponto de partida. Só entra nos
     * tipos que oferecem o filtro; nos demais é ignorado, e não vira um recorte
     * invisível que o operador não teria como desfazer.
     */
    defaultBillingStatus?: string
}

// Tipos cujo `where` do backend aceita `partnerId` — ver os `*Spec` em `reports.service.ts`.
const PARTNER_AWARE_TYPES: ReportType[] = [
    'consultations',
    'orders',
    'exams',
    'clients',
    'billings',
    'leads',
]

const DEFAULT_PAGE_SIZE = 20


function getDefaultFilters(
    reportType: ReportType,
    config?: ReportConfig,
    defaultBillingStatus?: string,
): ReportFilters {
    const now = new Date()
    return {
        startDate: toLocalDateString(new Date(now.getFullYear(), now.getMonth(), 1)),
        endDate: toLocalDateString(now),
        // O período conta pelo lançamento do registro, não pela data do atendimento nem pelo
        // vencimento: é o que o operador reconhece como "o que fizemos no mês". Quem precisa
        // de outra leitura (caixa, competência) troca no seletor "Contar período por".
        dateBasis: DEFAULT_DATE_BASIS,
        // Só entra nos tipos que têm o filtro; nos demais nada é aplicado, para não
        // virar um recorte invisível que o operador não teria como desfazer.
        ...defaultBillingStatusPatch(reportType, config, defaultBillingStatus ?? ''),
        ...saleDefaultsPatch(reportType, config),
    }
}

export default function ReportSection({
    reportType,
    config,
    scopeFilter,
    title,
    onTotalChange,
    onStatsChange,
    hidePeriodFilter = false,
    hideCommonFilters = false,
    defaultBillingStatus,
}: ReportSectionProps) {
    // Inicializador preguiçoso: o padrão vale como ponto de PARTIDA. Mudá-lo depois
    // remonta a seção pela `key` da página, em vez de sobrescrever o que o operador
    // ajustou à mão no meio do trabalho.
    const [filters, setFilters] = useState<ReportFilters>(() =>
        getDefaultFilters(reportType, config, defaultBillingStatus),
    )
    const [data, setData] = useState<any[]>([])
    const [stats, setStats] = useState<ReportStats | null>(null)
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
    const [pageCount, setPageCount] = useState(0)
    const [loading, setLoading] = useState(false)
    const [loadingStats, setLoadingStats] = useState(false)
    const [payoutBatchOpen, setPayoutBatchOpen] = useState(false)
    const [automationOpen, setAutomationOpen] = useState(false)

    /**
     * Modo AGRUPADO: uma linha por item de catálogo, somando o período inteiro,
     * em vez de um atendimento por linha. Só onde existe item
     * (`GROUPABLE_REPORT_TYPES`).
     *
     * Não é persistido de propósito, ao contrário das colunas: agrupar é uma
     * consulta pontual ("quanto deu hemograma este mês"), e reabrir a tela num
     * modo que não é o padrão confundiria mais do que ajudaria.
     */
    const canGroup = GROUPABLE_REPORT_TYPES.includes(reportType)
    const [groupedMode, setGroupedMode] = useState(false)
    const grouped = groupedMode && canGroup
    const [groupedRows, setGroupedRows] = useState<GroupedReportRow[]>([])
    const [groupedTruncated, setGroupedTruncated] = useState(false)

    /**
     * Colunas visíveis. Vale para a tabela E para os exports — a mesma lista de
     * chaves dos dois lados (ver `column-picker.tsx`).
     *
     * Persistida por tipo de relatório: a escolha é preferência de quem opera,
     * e refazê-la a cada visita era justamente a queixa. Chave desconhecida
     * (coluna renomeada ou removida numa versão nova) é descartada na leitura,
     * senão a tabela abriria sem ela para sempre.
     */
    // Cada modo tem o SEU conjunto de colunas e a sua chave: esconder "Paciente"
    // na lista detalhada não diz nada sobre o resumo por item, que nem tem essa
    // coluna.
    const columnSet = grouped ? GROUPED_COLUMNS : config.columns
    const storageKey = `reports:columns:${reportType}${grouped ? ':grouped' : ''}`
    const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
        readStoredColumns(storageKey, columnSet)
    )

    useEffect(() => {
        setVisibleColumns(readStoredColumns(storageKey, columnSet))
        // Trocar de aba ou de modo troca o conjunto de colunas por inteiro.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportType, grouped])

    const changeVisibleColumns = (keys: string[]) => {
        setVisibleColumns(keys)
        try {
            // Guarda só quando há recorte: sem isso, uma coluna NOVA numa versão
            // futura nasceria escondida para quem já abriu a tela uma vez.
            if (keys.length === columnSet.length) {
                localStorage.removeItem(storageKey)
            } else {
                localStorage.setItem(storageKey, JSON.stringify(keys))
            }
        } catch {
            // Modo privativo / storage cheio: a escolha vale para esta sessão.
        }
    }

    const visibleConfig = useMemo(
        () => ({
            ...config,
            columns: config.columns.filter(c => visibleColumns.includes(c.key)),
        }),
        [config, visibleColumns]
    )

    const showPartner = PARTNER_AWARE_TYPES.includes(reportType)

    // O agrupamento só existe para conta a PAGAR: é o repasse ao parceiro/profissional
    // que vira uma solicitação única. Em "todos os tipos" ou em RECEIVABLE o botão não
    // aparece — agrupar recebimento não significa nada.
    const canGeneratePayoutBatch =
        reportType === 'billings' && filters.type === 'PAYABLE'

    // Reset page on filter/type change. `createdById` vem do `scopeFilter` (a aba de
    // produtividade o injeta de fora), por isso entra aqui e não em `filters`.
    useEffect(() => {
        setPage(1)
    }, [reportType, filters.startDate, filters.endDate, filters.status, filters.type, filters.clientId, filters.professionalId, filters.partnerId, filters.paymentMethod, filters.paymentStatus, filters.isReturn, filters.isSurgical, filters.excludeLostSales, filters.gender, filters.minAge, filters.maxAge, filters.catalogId, filters.billingStatus, filters.billingPaymentMethod, filters.dateBasis, scopeFilter?.createdById, scopeFilter?.startDate, scopeFilter?.endDate])

    const mergedFilters = useMemo(() => ({ ...filters, ...(scopeFilter || {}) }), [filters, scopeFilter])

    const loadData = useCallback(async () => {
        setLoading(true)
        const publish = (value: number) => {
            setTotal(value)
            onTotalChange?.(value)
        }
        try {
            if (grouped) {
                const response = await getGroupedReport(reportType, mergedFilters)
                const result = response.success ? response.result : null
                setGroupedRows(result?.items ?? [])
                setGroupedTruncated(result?.truncated ?? false)
                // O contador do topo continua sendo o de ATENDIMENTOS; aqui ele
                // seria o de itens distintos, e trocá-lo faria a aba mostrar um
                // número que não é o do filtro.
                setPageCount(0)
                return
            }

            const response = await getReportDataPaged(reportType, { ...mergedFilters, page, pageSize })
            if (response.success && response.result) {
                const result = response.result as PagedResult<any>
                setData(result.items)
                publish(result.total)
                setPageCount(result.pageCount)
            } else {
                setData([])
                publish(0)
                setPageCount(0)
            }
        } catch {
            setData([])
            publish(0)
            setPageCount(0)
        } finally {
            setLoading(false)
        }
    }, [reportType, mergedFilters, page, pageSize, grouped, onTotalChange])

    const loadStats = useCallback(async () => {
        setLoadingStats(true)
        const publish = (value: ReportStats | null) => {
            setStats(value)
            onStatsChange?.(value)
        }
        try {
            const response = await getReportStats(reportType, mergedFilters)
            if (response.success) {
                publish(response.result ?? null)
            } else {
                publish(null)
            }
        } catch {
            publish(null)
        } finally {
            setLoadingStats(false)
        }
    }, [reportType, mergedFilters, onStatsChange])

    useEffect(() => {
        loadData()
    }, [loadData])

    useEffect(() => {
        loadStats()
    }, [loadStats])

    /**
     * Recarrega dados e estatísticas ao fechar o modal de detalhe. Não dá para saber
     * se houve mutação lá dentro, então recarrega sempre — o custo é uma requisição,
     * e a alternativa é a tabela exibir um registro já excluído.
     */
    const reloadAfterDetail = useCallback(() => {
        loadData()
        loadStats()
    }, [loadData, loadStats])

    const pagination = useMemo(
        () => ({
            page,
            pageSize,
            pageCount,
            onPageChange: setPage,
            onPageSizeChange: (size: number) => {
                setPageSize(size)
                setPage(1)
            },
        }),
        [page, pageSize, pageCount]
    )

    return (
        // O modal de detalhe abre a página do registro clicado sem tirar o usuário do
        // relatório (filtros e paginação preservados). Ao fechar, recarrega a listagem:
        // o registro pode ter sido editado ou excluído lá dentro.
        <DetailPopupProvider onClose={reloadAfterDetail}>
        <div className="flex flex-col gap-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-lg font-semibold text-base-content">
                    {title ?? config.label}
                </h2>
                <div className="flex items-center gap-2 flex-wrap">
                    {canGeneratePayoutBatch ? (
                        <button
                            className="btn btn-sm btn-primary"
                            onClick={() => setPayoutBatchOpen(true)}
                            disabled={loading}
                            title="Somar os repasses em aberto do filtro numa única conta a pagar por destinatário"
                        >
                            <MdPayments size={18} />
                            Gerar conta a pagar agrupada
                        </button>
                    ) : null}
                    <button
                        className="btn btn-sm btn-ghost gap-2"
                        onClick={() => setAutomationOpen(true)}
                        disabled={loading}
                        title="Agendar o envio deste relatório por email, com os filtros atuais"
                    >
                        <MdSchedule size={18} />
                        Automatizar
                    </button>
                    {canGroup && (
                        <div className="join">
                            <button
                                type="button"
                                className={`btn btn-sm join-item ${
                                    grouped ? 'btn-ghost border border-base-300' : 'btn-secondary'
                                }`}
                                onClick={() => setGroupedMode(false)}
                                title="Uma linha por atendimento"
                            >
                                <MdViewList size={16} />
                                Detalhado
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm join-item ${
                                    grouped ? 'btn-secondary' : 'btn-ghost border border-base-300'
                                }`}
                                onClick={() => setGroupedMode(true)}
                                title="Uma linha por item de catálogo, somando o período"
                            >
                                <MdFunctions size={16} />
                                Agrupado
                            </button>
                        </div>
                    )}
                    <ColumnPicker
                        columns={columnSet}
                        value={visibleColumns}
                        onChange={changeVisibleColumns}
                        disabled={loading}
                    />
                    <ExportActionsComponent
                        reportType={reportType}
                        filters={mergedFilters}
                        columns={visibleColumns}
                        grouped={grouped}
                        disabled={loading}
                    />
                </div>
            </div>

            <ReportFiltersComponent
                filters={filters}
                onChange={setFilters}
                config={config}
                showPartner={showPartner}
                reportType={reportType}
                hidePeriod={hidePeriodFilter}
                hideCommonFilters={hideCommonFilters}
                // As opções dos selectors seguem o escopo dos dados (o seletor de empresa /
                // organização do topo da página), não a unidade ativa do usuário.
                dataScope={{
                    scope: scopeFilter?.scope,
                    companyId: scopeFilter?.companyId,
                }}
            />

            <ReportStatsComponent
                stats={stats}
                config={config}
                loading={loadingStats}
                reportType={reportType}
                activeBillingKind={filters.type}
                activeBillingStatus={filters.status}
                activePaymentStatus={filters.paymentStatus}
                onPaymentStatusClick={(status) =>
                    setFilters({ ...filters, paymentStatus: status })
                }
            />

            {grouped ? (
                <GroupedReportTable
                    data={groupedRows}
                    loading={loading}
                    truncated={groupedTruncated}
                    visibleColumns={visibleColumns}
                />
            ) : (
                <ReportTableComponent
                    data={data}
                    config={visibleConfig}
                    loading={loading}
                    total={total}
                    reportType={reportType}
                    pagination={pagination}
                />
            )}

            {automationOpen && (
                <AutomationFormModal
                    initial={null}
                    // O agendado sai com as MESMAS colunas da tela: o operador
                    // recorta, automatiza, e o arquivo que chega por email é o
                    // que ele conferiu aqui.
                    presetFilters={{
                        ...mergedFilters,
                        ...(visibleColumns.length < config.columns.length
                            ? { columns: visibleColumns.join(',') }
                            : {}),
                    }}
                    presetReportType={reportType}
                    onClose={() => setAutomationOpen(false)}
                    onSaved={() => setAutomationOpen(false)}
                />
            )}

            {canGeneratePayoutBatch ? (
                <PayoutBatchModal
                    isOpen={payoutBatchOpen}
                    onClose={() => setPayoutBatchOpen(false)}
                    filters={{
                        startDate: mergedFilters.startDate,
                        endDate: mergedFilters.endDate,
                        // O relatório aceita bases que o agrupamento não tem (`paid` só
                        // lista o que já foi pago, e repasse pago não se agrupa). O que
                        // não for `due` cai em `created`, o padrão dos dois lados.
                        dateBasis:
                            mergedFilters.dateBasis === 'due' ? 'due' : 'created',
                        scope: mergedFilters.scope,
                        // A filial do seletor do topo. Sem ela, o agrupamento consultaria
                        // a unidade ativa do usuário e diria "nenhum repasse" com a
                        // listagem da outra filial na tela.
                        companyId: mergedFilters.companyId,
                        partnerId: mergedFilters.partnerId,
                        professionalId: mergedFilters.professionalId,
                        hospitalId: mergedFilters.hospitalId,
                        supplierId: mergedFilters.supplierId,
                    }}
                    // A base "data de pagamento" não existe no agrupamento (repasse
                    // pago não se agrupa): o modal avisa em vez de silenciosamente
                    // contar o período por outro campo.
                    periodBasisChanged={mergedFilters.dateBasis === 'paid'}
                    onGenerated={() => {
                        loadData()
                        loadStats()
                    }}
                />
            ) : null}
        </div>
        </DetailPopupProvider>
    )
}

/**
 * Lê a seleção salva, descartando chave que não existe mais e caindo para
 * "todas" quando não há nada guardado.
 */
function readStoredColumns(storageKey: string, columns: { key: string }[]): string[] {
    const all = columns.map(c => c.key)
    try {
        const raw = localStorage.getItem(storageKey)
        if (!raw) return all
        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) return all
        const valid = all.filter(key => parsed.includes(key))
        return valid.length > 0 ? valid : all
    } catch {
        return all
    }
}
